// Human-readable rendering of an employee's preferences for the HR/Manager
// case view — Employee Portal & App spec §9.4.1 / §14. Third-person, concise
// labels (the employee-facing form copy lives in the portal component; this is
// the org-facing read). Shared so HR and Manager render identically.

import type { SpecPreferenceFields } from './preferences.ts';

const FIELD_LABELS: Record<keyof SpecPreferenceFields, string> = {
  preferredContactPerson: 'Preferred contact',
  preferredContactChannel: 'Preferred channel',
  contactFrequency: 'Contact frequency',
  widerTeamComms: 'Wider team',
  colleagueContact: 'Colleague contact',
  rtwDiscussion: 'Return-to-work discussion',
  keyDateAwareness: 'Significant dates',
  shareRelationshipWithTeam: 'Share relationship with team',
  teamCommunicator: 'Who tells the team',
  rtwReadiness: 'Ready to discuss return',
  rtwContact: 'Return-to-work contact',
  rtwTeamAwareness: 'Team awareness (return)',
  rtwFirstDay: 'First day back',
  rtwWorkloadConcerns: 'Workload concerns',
  rtwFlexibilityNeeded: 'Flexibility needed',
  rtwHrSupportRequested: 'HR support requested',
};

// Prettify a snake_case enum value into a readable label. `hr` → "HR" is the
// one special case; everything else is title-cased word-by-word.
function prettify(value: string): string {
  if (value === 'hr') return 'HR';
  return value
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatValue(value: string | boolean): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return prettify(value);
}

export interface DisplayedPreference {
  key: keyof SpecPreferenceFields;
  label: string;
  value: string;
}

// Turn the role-visible `visible` map into ordered label/value rows for render.
// Null/absent fields are skipped (the projection already drops them, but guard
// anyway).
export function describeSpecPreferences(
  visible: Partial<SpecPreferenceFields>,
): DisplayedPreference[] {
  const out: DisplayedPreference[] = [];
  for (const key of Object.keys(FIELD_LABELS) as (keyof SpecPreferenceFields)[]) {
    const value = visible[key];
    if (value === null || value === undefined) continue;
    out.push({ key, label: FIELD_LABELS[key], value: formatValue(value) });
  }
  return out;
}

// Just the labels for the `limited` keys (value deliberately withheld).
export function describeLimitedPreferenceKeys(limited: (keyof SpecPreferenceFields)[]): string[] {
  return limited.map((key) => FIELD_LABELS[key]);
}
